'use client';
import React, { memo } from 'react';
import Image from 'next/image';

import { IButton, ITypo } from '@/app/_components/common';
import ISelect from '@/app/_components/common/ContactSection/ISelect';
import { Wrapper } from '@/app/_components/layout';
import { SelectedIcon } from '@/app/assets/Icons';
import { Images } from '@/app/assets/images';
import { useCreateContactMutation } from '@/store/api/contact.api';
import { cn, TimeInput } from '@nextui-org/react';

import ITextInput from './ITextInput';

import { ErrorMessage, Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

export const courses = [
	{ key: 'lt-pte', label: 'Luyện thi PTE' },
	{ key: 'lt-ielts', label: 'Luyện thi IELTS' },
	{ key: 'lt-sat', label: 'Luyện thi SAT' },
	{ key: 'pa-chuyen-sau', label: 'Phát âm Chuyên sâu' },
	{ key: 'gt-quoc-te', label: 'Giao tiếp Quốc tế' },
];

export const formalityOptions = [
	{ key: 'offline', label: 'Offline' },
	{ key: 'online', label: 'Online' },
];

export const timeOptions = [
	{
		key: 'time-morning',
		label: 'Sáng: 08:30 - 09:30 | 09:30 - 10:30 | 10:30 - 11:30',
	},
	{
		key: 'time-afternoon',
		label: 'Chiều: 14:30 - 15:30 | 15:30 - 16:30 | 16:30 - 17:30',
	},
	{
		key: 'time-evening',
		label: 'Tối: 18:30 - 19:30 | 19:30 - 20:30 | 20:30-21:30',
	},
];

export const ContactValidationSchema = Yup.object().shape({
	fullName: Yup.string().required('Họ và tên là bắt buộc'),
	phoneNumber: Yup.string()
		.matches(/^[0-9]{10}$/, 'Số điện thoại không hợp lệ')
		.required('Số điện thoại là bắt buộc'),
	province: Yup.string().required('Tỉnh/Thành phố là bắt buộc'),
	target: Yup.string().required('Vui lòng chọn một khoá học'),
	formality: Yup.string().required('Vui lòng chọn hình thức học'),
	time: Yup.string().required('Vui lòng chọn khung giờ'),
});

interface IProps {
	hiddenTitle?: boolean;
	hiddenBanner?: boolean;
}

const ContactSection: React.FC<IProps> = ({ hiddenTitle, hiddenBanner }) => {
	const [createContact, { isLoading: isCreateContact }] =
		useCreateContactMutation();

	return (
		<div
			id="contact-section"
			className={cn('relative mt-[50px]', {
				'mt-0': hiddenBanner || hiddenTitle,
			})}
		>
			<Formik
				initialValues={{
					fullName: '',
					phoneNumber: '',
					province: '',
					target: '',
					formality: '',
					time: '',
				}}
				validationSchema={ContactValidationSchema}
				onSubmit={(values) => {
					try {
						createContact({ data: { ...values } });
					} catch (error) {
						console.error('Create contact error: ', error);
					}
				}}
			>
				{({ handleSubmit }) => (
					<Form onSubmit={handleSubmit}>
						{!hiddenBanner && (
							<Image
								className="-z-[1]"
								src={Images.home.contactBanner}
								objectFit="cover"
								objectPosition="center"
								alt="DLA Da Nang"
								quality={100}
								priority
								fill
							/>
						)}
						<Wrapper
							className={cn('px-3 py-6 lg:py-[52px]', {
								'p-0 lg:p-0': hiddenBanner || hiddenTitle,
							})}
						>
							<div
								className={cn(
									'col min-h-[500px] gap-6 rounded-3xl bg-white bg-opacity-50 px-4 py-6 backdrop-blur-[10px] lg:ml-auto lg:max-w-[588px] lg:px-10',
									{
										'bg-transparent p-0 lg:p-0':
											hiddenBanner || hiddenTitle,
									}
								)}
							>
								{!hiddenTitle && (
									<div className="col gap-2 text-center">
										<ITypo
											size={18}
											className="sm:text-24"
											weight="semi"
											as="h3"
										>
											ĐĂNG KÝ TƯ VẤN RIÊNG CÙNG CỐ VẤN HỌC
											THUẬT DLA - MIỄN PHÍ
										</ITypo>
										<ITypo
											size={12}
											className="sm:text-14"
											as="p"
										>
											Chia sẻ mục tiêu, kiểm tra năng lực,
											phát thảo lộ trình học tập riêng
											giúp bạn hình dung tương lai và hình
											thành động lực luyện thi cao nhất.
										</ITypo>
									</div>
								)}

								<div className="col gap-3">
									<div>
										<Field name={'fullName'}>
											{({
												field, // { name, value, onChange, onBlur }
												form: { setFieldValue },
											}: any) => (
												<ITextInput
													{...field}
													placeholder="Họ và tên (*)"
												/>
											)}
										</Field>
										<ITypo
											size={12}
											weight="semi"
											className="text-red"
										>
											<ErrorMessage name={'fullName'} />
										</ITypo>
									</div>

									<div>
										<Field name={'phoneNumber'}>
											{({
												field, // { name, value, onChange, onBlur }
												form: { setFieldValue },
											}: any) => (
												<ITextInput
													{...field}
													placeholder="Số điện thoại (*)"
												/>
											)}
										</Field>
										<ITypo
											size={12}
											weight="semi"
											className="text-red"
										>
											<ErrorMessage
												name={'phoneNumber'}
											/>
										</ITypo>
									</div>

									<div>
										<Field name={'province'}>
											{({
												field, // { name, value, onChange, onBlur }
												form: { setFieldValue },
											}: any) => (
												<ITextInput
													{...field}
													placeholder="Tỉnh/Thành phố (*)"
												/>
											)}
										</Field>
										<ITypo
											size={12}
											weight="semi"
											className="text-red"
										>
											<ErrorMessage name={'province'} />
										</ITypo>
									</div>

									<div>
										<Field name={'target'}>
											{({
												field, // { name, value, onChange, onBlur }
												form: { setFieldValue },
											}: any) => (
												<ISelect
													items={courses}
													onChange={(e) =>
														setFieldValue(
															'target',
															e.target.value
														)
													}
													placeholder="Khoá học quan tâm"
												/>
											)}
										</Field>
										<ITypo
											size={12}
											weight="semi"
											className="text-red"
										>
											<ErrorMessage name={'target'} />
										</ITypo>
									</div>

									<div>
										<Field name={'formality'}>
											{({
												field, // { name, value, onChange, onBlur }
												form: { setFieldValue },
											}: any) => (
												<ISelect
													items={formalityOptions}
													onChange={(e) =>
														setFieldValue(
															'formality',
															e.target.value
														)
													}
													placeholder="Bạn muốn học theo hình thức gì?"
												/>
											)}
										</Field>
										<ITypo
											size={12}
											weight="semi"
											className="text-red"
										>
											<ErrorMessage name={'formality'} />
										</ITypo>
									</div>

									<div>
										<Field name={'time'}>
											{({
												field, // { name, value, onChange, onBlur }
												form: { setFieldValue, errors },
											}: any) => (
												<div className="flex h-10 flex-row items-center justify-between rounded-full bg-white px-3">
													<div className="text-14 text-gray flex flex-1">
													<p >
														Khung giờ bạn muốn được
														tư vấn?
													</p>
													</div>
													<TimeInput
													className='w-fit'
													classNames={{
														innerWrapper: "h-10"
													}}
														isRequired
														isInvalid={
															!!errors.time
														}
														onChange={(value) =>
															setFieldValue(
																'time',
																value
															)
														}
													/>
												</div>
											)}
										</Field>
										<ITypo
											size={12}
											weight="semi"
											className="text-red"
										>
											<ErrorMessage name={'time'} />
										</ITypo>
									</div>
								</div>

								<div className="flex gap-2">
									<SelectedIcon />
									<ITypo
										size={12}
										className="sm:text-14 flex-1"
										as="p"
									>
										Bằng việc đăng ký thông tin, bạn đồng ý
										cho phép DLA liên lạc thông tin qua các
										hình thức: Cuộc gọi, tin nhắn, email
										nhằm mục đích tư vấn các chương trình
										học.
									</ITypo>
								</div>

								<IButton
									type="submit"
									isLoading={isCreateContact}
									className="mx-auto h-fit min-h-9 w-fit rounded-xl px-6 lg:min-h-10"
								>
									<ITypo size={14} className="lg:text-16">
										Đăng ký ngay
									</ITypo>
								</IButton>
							</div>
						</Wrapper>
					</Form>
				)}
			</Formik>
		</div>
	);
};

export default memo(ContactSection);
